// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { API_BASE, type ShippingCarrier } from '../../api/client'
import type { CarrierTestResult } from '../../api/shipping'
import { friendlyError } from '../../api/errors'
import { useCarrierAccounts, useTestCarrierAccount, useUpdateCarrierAccount } from '../../hooks/queries/shipping'
import { carrierLabel, carrierSetup } from '../../lib/carriers'
import AppButton from '../ui/AppButton'
import { AppTextField, ToggleSwitch } from '../ui/FormFields'
import LoadState from '../ui/LoadState'
import Notice from '../ui/Notice'
import Section from '../ui/Section'
import StatusChip from '../ui/StatusChip'
import { useToast } from '../ui/Toast'
import CarrierAccountDialog from './CarrierAccountDialog'
import PickupLocationField from './PickupLocationField'

/** The URL RoadPas posts order events to for this account. */
const webhookUrl = (pharmacyId: string, accountId: string) =>
  new URL(`${API_BASE}/shipping/webhooks/roadpas/${pharmacyId}/${accountId}`, window.location.origin).toString()

/**
 * D-SHIP-23 §10 — a pharmacy's shipping providers: enable or disable each, add one, configure it,
 * test the connection. Used by the SaaS-admin pharmacy profile (Shipping tab).
 */
export default function CarrierAccountsPanel({ pharmacyId }: { pharmacyId: string }) {
  const accounts = useCarrierAccounts(pharmacyId)
  const [editing, setEditing] = useState<ShippingCarrier | 'new' | null>(null)
  const kinds = Array.from(new Set([...(accounts.data?.supported_carriers ?? []), 'manual']))

  return (
    <Section title="Shipping providers"
      subtitle="Providers this pharmacy can ship with. Disabled providers disappear from the ship drawer and stop syncing.">
      <Box sx={{ mb: 1.5 }}>
        <AppButton variant="contained" size="small" onClick={() => setEditing('new')} disabled={!accounts.data}>Add provider</AppButton>
      </Box>
      <LoadState query={{ ...accounts, data: accounts.data?.items }} what="the shipping providers"
        empty={<Box sx={{ fontSize: 13, color: 'text.secondary' }}>No shipping providers yet.</Box>}>
        {items => (
          <Stack spacing={1.5}>
            {items.map(c => <CarrierRow key={c.id} account={c} pharmacyId={pharmacyId} onConfigure={() => setEditing(c)} />)}
          </Stack>
        )}
      </LoadState>
      {editing && (
        <CarrierAccountDialog account={editing === 'new' ? null : editing} carriers={kinds} pharmacyId={pharmacyId}
          onClose={() => setEditing(null)} />
      )}
    </Section>
  )
}

function CarrierRow({ account: c, pharmacyId, onConfigure }: { account: ShippingCarrier; pharmacyId: string; onConfigure: () => void }) {
  const toast = useToast()
  const update = useUpdateCarrierAccount(pharmacyId)
  const test = useTestCarrierAccount(pharmacyId)
  const [result, setResult] = useState<CarrierTestResult | null>(null)
  const setup = carrierSetup(c.carrier)
  const missing = setup.credentials.filter(f => !(c.credential_keys ?? []).includes(f.key))

  const setActive = (is_active: boolean) => update.mutate({ id: c.id, input: { is_active } }, {
    onSuccess: () => toast.show(`${c.display_name} ${is_active ? 'enabled' : 'disabled'}.`, 'success'),
    onError: e => toast.show(friendlyError(e), 'error'),
  })
  const setBranch = (account_number: string) => update.mutate({ id: c.id, input: { account_number } }, {
    onSuccess: () => toast.show('Default branch saved.', 'success'),
    onError: e => toast.show(friendlyError(e), 'error'),
  })
  const runTest = () => test.mutate(c.id, { onSuccess: setResult, onError: e => setResult({ ok: false, detail: friendlyError(e), token_hint: null, account_number: null }) })

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box component="strong" sx={{ fontSize: 14 }}>{c.display_name}</Box>
        <StatusChip status={c.carrier} label={carrierLabel(c.carrier)} tone="grey" />
        <StatusChip status={c.is_test_mode ? 'test' : 'live'} label={c.is_test_mode ? 'Test' : 'Live'} tone={c.is_test_mode ? 'amber' : 'green'} />
        <Box sx={{ flex: 1 }} />
        <ToggleSwitch label={c.is_active ? 'Enabled' : 'Disabled'} checked={c.is_active} disabled={update.isPending} onChange={setActive} />
      </Stack>

      <Box sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
        {missing.length > 0 ? `Missing: ${missing.map(f => f.label).join(', ')}. ` : ''}
        Last verified: {c.last_verified_at ? new Date(c.last_verified_at).toLocaleString() : 'never'}
      </Box>

      {setup.pickupFromCarrier && c.has_credentials && (
        <Box sx={{ mt: 1.5, maxWidth: 420 }}>
          <PickupLocationField carrierId={c.id} pharmacyId={pharmacyId} label={setup.accountLabel}
            value={c.account_number ?? ''} onChange={setBranch} />
        </Box>
      )}

      {setup.webhook && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <AppTextField size="small" fullWidth label="Webhook URL (paste into the RoadPas portal)"
            value={webhookUrl(pharmacyId, c.id)} InputProps={{ readOnly: true }} />
          <AppButton size="small" onClick={() => {
            navigator.clipboard.writeText(webhookUrl(pharmacyId, c.id))
              .then(() => toast.show('Webhook URL copied.', 'success'))
              .catch(() => toast.show('Copy failed. Select the URL and copy it.', 'error'))
          }}>Copy</AppButton>
        </Stack>
      )}

      {result && <Box sx={{ mt: 1.5 }}><Notice severity={result.ok ? 'success' : 'error'}>{result.detail}</Notice></Box>}
      {!result && c.last_verify_error && <Box sx={{ mt: 1.5 }}><Notice severity="warning">Last test: {c.last_verify_error}</Notice></Box>}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <AppButton size="small" variant="outlined" onClick={runTest} disabled={test.isPending || c.carrier === 'manual'}>
          {test.isPending ? 'Testing…' : 'Test connection'}
        </AppButton>
        <AppButton size="small" onClick={onConfigure}>Configure</AppButton>
      </Stack>
    </Box>
  )
}
